import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Setting } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { UiSource } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/ui_source.pb';
import { last } from 'lodash-es';
import {
  formatResultsPrimaryKeys,
  handleRegisterClick,
  handleTriggerSearch,
  handleUpdateSearch,
  SearchHistory,
  useSearchHistoryShallowStore,
} from '@frontend/search-history';
import { SearchQueries, useFilterSettings, useFilterSettingsOld } from '@frontend/search-hooks';
import {
  StaticSettingsRoutes,
  useInterRouter,
  useSettingsNavigate,
  useSettingsSearchAccessRoutePaths,
} from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  useFormField,
  TextField,
  SearchIcon,
  ComboboxBase,
  usePopoverMenu,
  Text,
  Truncated,
  PopoverMenuItem,
  CaretRightIconSmall,
  styles,
  useDebouncedFn,
  useDebouncedValue,
} from '@frontend/design-system';

// TODO: remove this old module when semantic-search FF is fully implemented
export const SettingsSearchOld = () => {
  const { navigate } = useSettingsNavigate();
  const { isSettingsMenuOpen, setIsSettingsMenuOpen } = useInterRouter(['isSettingsMenuOpen', 'setIsSettingsMenuOpen']);
  const sectionRef = useRef<HTMLElement | null>(null);
  const { onChange: searchFieldOnChange, onFocus, onBlur, ...searchParams } = useFormField({ type: 'text' });
  const { getMenuProps, getItemProps, getTriggerProps, open, close, activeIndex, setActiveIndex, refs } =
    usePopoverMenu<HTMLInputElement>({
      placement: 'bottom',
      middlewareOptions: {
        offset: {
          mainAxis: 9,
          crossAxis: -12,
        },
      },
      interactionOptions: { listNavigation: { virtual: true }, typeahead: { enabled: false } },
    });
  const [focused, setFocused] = useState(false);

  const closeSettingsMenu = () => {
    if (isSettingsMenuOpen) {
      setIsSettingsMenuOpen(false);
    }
  };

  const searchedRoutePathsPreFiltered = useFilterSettingsOld(searchParams.value);
  const searchedRoutePaths = useSettingsSearchAccessRoutePaths(searchedRoutePathsPreFiltered);

  const { ref, ...triggerProps } = getTriggerProps({
    onKeyDown: (event) => {
      if (event.key === 'Enter' && activeIndex != null) {
        navigate({
          to: searchedRoutePaths[activeIndex]?.path.slice(0, -1).replace('settings/', '') as StaticSettingsRoutes,
        });
        setActiveIndex(null);
        closeSettingsMenu();
        close();
      }
    },
    onChange: (e) => {
      searchFieldOnChange(e);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore our change events are still messed up :(
      if (e.target.value) {
        open();
      } else {
        close();
      }
    },
  });

  const getComboboxListContainerWidth = sectionRef.current ? sectionRef.current?.getBoundingClientRect().width : 200;
  const searchInputTextColor = focused ? theme.colors.neutral90 : theme.colors.neutral20;

  return (
    <section
      css={[
        css`
          padding: ${theme.spacing(3)};
          border-bottom: 1px solid ${theme.colors.neutral60};
        `,
      ]}
    >
      <ComboboxBase
        menuProps={getMenuProps()}
        menuStyles={css`
          width: ${getComboboxListContainerWidth}px;
          border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
          padding-bottom: 0;
          max-height: 400px !important;
          overflow-y: auto;
          padding-top: 0;
        `}
        Input={
          <section ref={sectionRef}>
            <TextField
              aria-autocomplete='list'
              role='combobox'
              startAdornment={<SearchIcon />}
              {...triggerProps}
              containerCss={css`
                background: ${focused ? theme.colors.white : theme.colors.neutral80};
                border-radius: ${theme.borderRadius.small};
                transition: 0.2s ease;
                caret-color: ${searchInputTextColor};

                svg {
                  fill: ${searchInputTextColor};
                }

                & input {
                  color: ${searchInputTextColor} !important;
                }
                & input::placeholder {
                  color: ${searchInputTextColor};
                }
              `}
              fieldComponentProps={{ ref: refs.setReference }}
              name=''
              label=''
              placeholder='Search'
              onFocus={(e) => {
                setFocused(true);
                onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              {...searchParams}
            />
          </section>
        }
      >
        {searchedRoutePaths.map((item, index) => {
          return (
            <PopoverMenuItem
              key={item.path}
              {...getItemProps({
                index,
                onClick: () => {
                  navigate({ to: item.path.slice(0, -1).replace('settings/', '') as StaticSettingsRoutes });
                  setActiveIndex(null);
                  closeSettingsMenu();
                  close();
                },
              })}
              css={css`
                height: auto;
                padding: ${theme.spacing(3, 2)};
                border-bottom: 1px solid ${theme.colors.neutral10};
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-flow: row;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                  align-items: center;
                  width: 100%;
                  cursor: pointer;
                `}
              >
                <SettingsSearchItemOld item={item} />
              </div>
            </PopoverMenuItem>
          );
        })}
      </ComboboxBase>
    </section>
  );
};

// TODO: remove this old module when semantic-search FF is fully implemented
const SettingsSearchBreadcrumbItemOld = ({ crumb, isLastItem }: { crumb: string; isLastItem: boolean }) => (
  <>
    <span
      css={css`
        color: ${isLastItem ? 'black' : theme.colors.neutral50};
        flex: 0 1 auto !important;
      `}
      aria-hidden='true'
    >
      {crumb}
    </span>
    {!isLastItem && <CaretRightIconSmall size={8} aria-hidden='true' />}
  </>
);

// TODO: remove this old module when semantic-search FF is fully implemented
export const SettingsSearchItemOld = ({ item }: { item: { labelPath: string; path: string } }) => {
  const breadcrumb = item.labelPath.split('/');

  return (
    <>
      <div
        role='text'
        aria-label={`Path: ${item.labelPath}`}
        css={css`
          width: 100%;
          position: relative;
        `}
      >
        <Text textAlign='left' css={styles.truncate}>
          {last(breadcrumb)}
        </Text>
        <div
          css={css`
            display: flex;
            align-items: center;
            width: 100%;
            gap: ${theme.spacing(1)};
            font-size: 12px;
            justify-content: flex-start;
          `}
        >
          {breadcrumb.map((crumb, idx) => (
            <SettingsSearchBreadcrumbItemOld key={idx} crumb={crumb} isLastItem={idx + 1 === breadcrumb.length} />
          ))}
        </div>
      </div>
    </>
  );
};

const entity = Entity.ENTITY_SETTING;
const uiSource = UiSource.SETTINGS_SEARCH;

export const SettingsSearch = () => {
  const { navigate } = useSettingsNavigate();
  const { isSettingsMenuOpen, setIsSettingsMenuOpen } = useInterRouter(['isSettingsMenuOpen', 'setIsSettingsMenuOpen']);
  const sectionRef = useRef<HTMLElement | null>(null);
  const { onChange: searchFieldOnChange, onFocus, onBlur, ...searchParams } = useFormField({ type: 'text' });
  const { getMenuProps, getItemProps, getTriggerProps, open, close, activeIndex, setActiveIndex, refs } =
    usePopoverMenu<HTMLInputElement>({
      placement: 'bottom',
      middlewareOptions: {
        offset: {
          mainAxis: 9,
          crossAxis: -12,
        },
      },
      interactionOptions: { listNavigation: { virtual: true }, typeahead: { enabled: false } },
    });
  const [focused, setFocused] = useState(false);

  const closeSettingsMenu = () => {
    if (isSettingsMenuOpen) {
      setIsSettingsMenuOpen(false);
    }
  };

  const debouncedSearchTerm = useDebouncedValue(searchParams.value, 300);

  const { debouncedValue: debouncedSearchHistoryValue, setDebouncedValue: setDebouncedSearchHistoryValue } =
    useSearchHistoryShallowStore('debouncedValue', 'setDebouncedValue');
  const onSearchHistoryChange = useDebouncedFn((value) => setDebouncedSearchHistoryValue(value), 300);

  useEffect(() => {
    handleTriggerSearch({
      entities: [entity],
      searchTerm: debouncedSearchTerm,
      uiSource,
    });
  }, [debouncedSearchTerm]);

  const { data: settingsSearch } = SearchQueries.useSearch(
    {
      entity: [entity],
      query: debouncedSearchHistoryValue,
    },
    {
      enabled: debouncedSearchHistoryValue.length > 0,
      onSettled: (response) => {
        const searchedSettings = response?.setting ?? [];
        handleUpdateSearch({
          entity: Entity.ENTITY_SETTING,
          results: formatResultsPrimaryKeys<Setting>('path', searchedSettings),
        });
      },
    }
  );

  const { filterSetting } = useFilterSettings();

  const processedSettings = filterSetting(settingsSearch?.setting ?? [], debouncedSearchTerm);

  const searchedRoutePaths = useSettingsSearchAccessRoutePaths(processedSettings);

  const { ref, ...triggerProps } = getTriggerProps({
    onKeyDown: (event) => {
      if (event.key === 'Enter' && activeIndex != null) {
        navigate({
          to: searchedRoutePaths[activeIndex]?.path?.slice(0, -1).replace('settings/', '') as StaticSettingsRoutes,
        });
        setActiveIndex(null);
        closeSettingsMenu();
        close();
      }
    },
    onChange: (e) => {
      searchFieldOnChange(e);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore our change events are still messed up :(
      const value = e.target.value;
      if (value) {
        onSearchHistoryChange(value);
        open();
      } else {
        close();
      }
    },
  });

  const getComboboxListContainerWidth = sectionRef.current ? sectionRef.current?.getBoundingClientRect().width : 200;
  const menuItemWidth = getComboboxListContainerWidth + 40;
  const searchInputTextColor = focused ? theme.colors.neutral90 : theme.colors.neutral20;

  return (
    <section
      css={[
        css`
          padding: ${theme.spacing(3)};
          border-bottom: 1px solid ${theme.colors.neutral60};
        `,
      ]}
    >
      <SearchHistory id={uiSource} />
      <ComboboxBase
        menuProps={getMenuProps()}
        menuStyles={css`
          width: ${menuItemWidth}px;
          border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
          padding-bottom: 0;
          margin-left: 15px;
          max-height: 400px !important;
          overflow-y: auto;
          padding-top: 0;
        `}
        Input={
          <section ref={sectionRef}>
            <TextField
              aria-autocomplete='list'
              role='combobox'
              startAdornment={<SearchIcon />}
              {...triggerProps}
              containerCss={css`
                background: ${focused ? theme.colors.white : theme.colors.neutral80};
                border-radius: ${theme.borderRadius.small};
                transition: 0.2s ease;
                caret-color: ${searchInputTextColor};

                svg {
                  fill: ${searchInputTextColor};
                }

                & input {
                  color: ${searchInputTextColor} !important;
                }
                & input::placeholder {
                  color: ${searchInputTextColor};
                }
              `}
              fieldComponentProps={{ ref: refs.setReference }}
              name=''
              label=''
              placeholder='Search'
              onFocus={(e) => {
                setFocused(true);
                onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              {...searchParams}
            />
          </section>
        }
      >
        {searchedRoutePaths.map((item, index) => {
          return (
            <PopoverMenuItem
              key={item.path}
              {...getItemProps({
                index,
                onClick: () => {
                  const path = item.path?.replace('settings/', '');
                  const formattedPath = path.endsWith('/') ? path.slice(0, -1) : path;
                  navigate({ to: formattedPath as StaticSettingsRoutes });
                  setActiveIndex(null);
                  closeSettingsMenu();
                  handleRegisterClick({
                    entity,
                    entityId: item.id,
                    entityPosition: 0,
                    itemPosition: index,
                  });
                  close();
                },
              })}
              css={css`
                height: auto;
                padding: ${theme.spacing(3, 2)};
                border-bottom: 1px solid ${theme.colors.neutral10};
                width: ${menuItemWidth}px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-flow: row;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                  align-items: center;
                  width: 100%;
                  cursor: pointer;
                `}
              >
                <SettingsSearchItem {...item} />
              </div>
            </PopoverMenuItem>
          );
        })}
      </ComboboxBase>
    </section>
  );
};

const SettingsSearchBreadcrumbItem = ({ crumb, isLastItem }: { crumb: string; isLastItem: boolean }) => (
  <>
    <span
      css={css`
        color: ${isLastItem ? 'black' : theme.colors.neutral50};
        flex: 0 1 auto !important;
      `}
      aria-hidden='true'
    >
      {crumb}
    </span>
    {!isLastItem && <CaretRightIconSmall size={8} aria-hidden='true' />}
  </>
);

export const SettingsSearchItem = ({ breadcrumbs, name, path }: Setting) => {
  const displayedBreadcrumbs = breadcrumbs ?? path?.split('/') ?? [];

  return (
    <>
      <div
        role='text'
        aria-label={`Path: ${breadcrumbs}`}
        css={css`
          width: 100%;
          position: relative;
        `}
      >
        <Truncated as={Text} textAlign='left' css={styles.truncate}>
          {name}
        </Truncated>
        <div
          css={css`
            display: flex;
            align-items: center;
            width: 100%;
            gap: ${theme.spacing(1)};
            font-size: 12px;
            justify-content: flex-start;
          `}
        >
          {displayedBreadcrumbs.map((crumb, idx) => (
            <SettingsSearchBreadcrumbItem
              key={idx}
              crumb={crumb}
              isLastItem={idx + 1 === displayedBreadcrumbs.length}
            />
          ))}
        </div>
        {/* TODO: we won't have the description available for all settings so leaving it off for the first iteration */}
        {/* <Text
          size='small'
          textAlign='left'
          color='subdued'
          css={css`
            margin-top: ${theme.spacing(0.5)};
          `}
        >
          {description}
        </Text> */}
      </div>
    </>
  );
};
